import { useState, useEffect, useRef } from "react";
import { api, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format, parseISO, addYears, differenceInDays } from "date-fns";
import { 
  Plus, Shield, Mail, Trash2, Loader2, CheckCircle2, Clock, LogOut, 
  Image, Upload, X, Users, Search, UserPlus, Pause, Play, CalendarPlus,
  AlertTriangle, Edit, Calendar, KeyRound
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppLogo from "@/components/AppLogo";
import { useLogo } from "@/contexts/LogoContext";

export default function AdminPage() {
  const { logout } = useAuth();
  const { logo, refreshLogo } = useLogo();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Email management state
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: "", notes: "" });
  
  // User management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false);
  const [submittingMember, setSubmittingMember] = useState(false);
  const [memberForm, setMemberForm] = useState({
    name: "",
    email: "",
    access_expires_at: format(addYears(new Date(), 1), "yyyy-MM-dd")
  });
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Suspend confirmation state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState(null);
  const [suspending, setSuspending] = useState(false);
  
  // Edit expiration state
  const [editExpiryDrawerOpen, setEditExpiryDrawerOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [updatingExpiry, setUpdatingExpiry] = useState(false);
  
  // Reset password state
  const [resetPasswordDrawerOpen, setResetPasswordDrawerOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [tempPassword, setTempPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Logo management state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  const fetchEmails = async () => {
    try {
      const res = await api.get("/admin/authorized-emails");
      setEmails(res.data);
    } catch (error) {
      toast.error("Failed to load authorized emails");
    } finally {
      setLoadingEmails(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchUsers();
  }, []);

  // Email management handlers
  const resetEmailForm = () => {
    setEmailForm({ email: "", notes: "" });
  };

  const handleSubmitEmail = async () => {
    if (!emailForm.email) {
      toast.error("Please enter an email");
      return;
    }

    setSubmittingEmail(true);
    try {
      await api.post("/admin/authorized-emails", emailForm);
      toast.success("Email authorized");
      setEmailDrawerOpen(false);
      resetEmailForm();
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to authorize email");
    } finally {
      setSubmittingEmail(false);
    }
  };

  const handleDeleteEmail = async (id) => {
    try {
      await api.delete(`/admin/authorized-emails/${id}`);
      toast.success("Email removed");
      fetchEmails();
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  // User management handlers
  const handleSuspendUserDirect = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, { account_status: "suspended" });
      toast.success("User suspended");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to suspend user");
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, { account_status: "active" });
      toast.success("User reactivated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to reactivate user");
    }
  };

  const handleExtendAccess = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/extend`);
      toast.success("Access extended by 1 year");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to extend access");
    }
  };

  // Suspend user handler (soft delete - preserves data)
  const handleSuspendUser = async () => {
    if (!userToSuspend) return;
    
    setSuspending(true);
    try {
      await api.put(`/admin/users/${userToSuspend.id}/suspend`);
      toast.success(`User ${userToSuspend.email} has been suspended`);
      setSuspendDialogOpen(false);
      setUserToSuspend(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suspend user");
    } finally {
      setSuspending(false);
    }
  };

  // Delete user handler (permanent - removes all data)
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userToDelete.id}`);
      toast.success(`User ${userToDelete.email} has been permanently deleted`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
      fetchEmails(); // Refresh emails list too since we remove from authorized_emails
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  // Edit expiration handler
  const handleEditExpiry = (user) => {
    setUserToEdit(user);
    setNewExpiryDate(user.access_expires_at || format(addYears(new Date(), 1), "yyyy-MM-dd"));
    setEditExpiryDrawerOpen(true);
  };

  const handleSaveExpiry = async () => {
    if (!userToEdit || !newExpiryDate) return;
    
    setUpdatingExpiry(true);
    try {
      await api.put(`/admin/users/${userToEdit.id}`, { access_expires_at: newExpiryDate });
      toast.success("Expiration date updated");
      setEditExpiryDrawerOpen(false);
      setUserToEdit(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update expiration");
    } finally {
      setUpdatingExpiry(false);
    }
  };

  // Reset password handler
  const handleOpenResetPassword = (user) => {
    setUserToReset(user);
    setTempPassword("");
    setResetPasswordDrawerOpen(true);
  };

  const handleResetPassword = async () => {
    if (!userToReset || !tempPassword) return;
    
    if (tempPassword.length < 6) {
      toast.error("Temporary password must be at least 6 characters");
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.post(`/admin/users/${userToReset.id}/reset-password`, { temp_password: tempPassword });
      toast.success(`Password reset for ${userToReset.email}`);
      setResetPasswordDrawerOpen(false);
      setUserToReset(null);
      setTempPassword("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  // Member form handlers
  const resetMemberForm = () => {
    setMemberForm({
      name: "",
      email: "",
      access_expires_at: format(addYears(new Date(), 1), "yyyy-MM-dd")
    });
  };

  const handleSubmitMember = async () => {
    if (!memberForm.name || !memberForm.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmittingMember(true);
    try {
      const res = await api.post("/admin/members", memberForm);
      if (res.data.reactivated) {
        toast.success("User reactivated with new expiration date");
      } else {
        toast.success("Member added - they can now register");
      }
      setMemberDrawerOpen(false);
      resetMemberForm();
      fetchEmails();
      fetchUsers(); // Refresh users list to show reactivated user
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setSubmittingMember(false);
    }
  };

  // Logo handlers
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!["image/png", "image/svg+xml"].includes(file.type)) {
      toast.error("Please upload a PNG or SVG file");
      return;
    }

    if (file.size > 500 * 1024) {
      toast.error("File is too large. Maximum size is 500KB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post("/admin/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Logo uploaded successfully");
      refreshLogo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    setRemovingLogo(true);
    try {
      await api.delete("/admin/logo");
      toast.success("Logo removed");
      refreshLogo();
    } catch (error) {
      toast.error("Failed to remove logo");
    } finally {
      setRemovingLogo(false);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Check if expiration is within 5 days
  const getExpirationWarning = (accessExpiresAt) => {
    if (!accessExpiresAt) return null;
    try {
      const expiryDate = parseISO(accessExpiresAt);
      const today = new Date();
      const daysUntilExpiry = differenceInDays(expiryDate, today);
      
      if (daysUntilExpiry < 0) {
        return { type: "expired", message: "Expired" };
      } else if (daysUntilExpiry <= 5) {
        return { type: "warning", message: `⚠ Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}` };
      }
      return null;
    } catch {
      return null;
    }
  };

  const getStatusBadge = (status, accessExpiresAt) => {
    const warning = getExpirationWarning(accessExpiresAt);
    
    switch (status) {
      case "active":
        if (warning?.type === "warning") {
          return <Badge variant="outline" className="border-orange-500 text-orange-600 bg-orange-50">{warning.message}</Badge>;
        }
        return <Badge variant="default" className="bg-green-500">Active</Badge>;
      case "suspended":
        return <Badge variant="destructive">Suspended</Badge>;
      case "expired":
        return <Badge variant="secondary">Expired</Badge>;
      default:
        return <Badge variant="default">Active</Badge>;
    }
  };

  return (
    <div className="page-container" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h1 className="font-heading text-lg font-bold">Admin</h1>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="h-9 w-9 rounded-full"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-4">
        {/* Logo Management */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Image size={18} />
              Application Logo
            </CardTitle>
            <CardDescription>
              Upload a custom logo (PNG or SVG, max 500KB)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden">
                {logo ? (
                  <img src={logo} alt="Logo" className="h-12 w-12 object-contain" />
                ) : (
                  <Image size={24} className="text-muted-foreground" />
                )}
              </div>
              
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="h-9"
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Upload size={16} className="mr-2" />
                    )}
                    {logo ? "Replace" : "Upload"}
                  </Button>
                  {logo && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveLogo}
                      disabled={removingLogo}
                      className="h-9 text-destructive hover:text-destructive"
                      data-testid="remove-logo-btn"
                    >
                      {removingLogo ? (
                        <Loader2 size={16} className="mr-2 animate-spin" />
                      ) : (
                        <X size={16} className="mr-2" />
                      )}
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Members and Emails */}
        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users size={16} />
              Members
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail size={16} />
              Authorized Emails
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4 mt-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-3">
                  <div className="text-xl font-bold font-heading">{users.length}</div>
                  <div className="text-xs text-muted-foreground">Total Members</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <div className="text-xl font-bold font-heading text-green-600">
                    {users.filter(u => u.account_status === "active").length}
                  </div>
                  <div className="text-xs text-muted-foreground">Active</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <div className="text-xl font-bold font-heading text-destructive">
                    {users.filter(u => u.account_status === "suspended" || u.account_status === "expired").length}
                  </div>
                  <div className="text-xs text-muted-foreground">Inactive</div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Add Member */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="user-search-input"
                />
              </div>
              <Drawer open={memberDrawerOpen} onOpenChange={(open) => {
                setMemberDrawerOpen(open);
                if (!open) resetMemberForm();
              }}>
                <DrawerTrigger asChild>
                  <Button size="sm" className="h-10 rounded-full" data-testid="add-member-btn">
                    <UserPlus size={16} className="mr-1" />
                    Add
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle className="font-heading">Add New Member</DrawerTitle>
                  </DrawerHeader>
                  
                  <div className="px-4 pb-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="member_name">Name *</Label>
                      <Input
                        id="member_name"
                        placeholder="John Doe"
                        value={memberForm.name}
                        onChange={(e) => setMemberForm(prev => ({ ...prev, name: e.target.value }))}
                        className="h-12"
                        data-testid="member-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="member_email">Email *</Label>
                      <Input
                        id="member_email"
                        type="email"
                        placeholder="user@example.com"
                        value={memberForm.email}
                        onChange={(e) => setMemberForm(prev => ({ ...prev, email: e.target.value }))}
                        className="h-12"
                        data-testid="member-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Access Expires</Label>
                      <DatePicker
                        value={memberForm.access_expires_at}
                        onChange={(date) => setMemberForm(prev => ({ ...prev, access_expires_at: date }))}
                        data-testid="member-expiry-input"
                      />
                    </div>
                  </div>

                  <DrawerFooter className="pt-2">
                    <Button 
                      onClick={handleSubmitMember} 
                      disabled={submittingMember}
                      className="h-12 rounded-full"
                      data-testid="member-save-btn"
                    >
                      {submittingMember ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        "Add Member"
                      )}
                    </Button>
                    <DrawerClose asChild>
                      <Button variant="outline" className="h-12 rounded-full">Cancel</Button>
                    </DrawerClose>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </div>

            {/* Members List */}
            <Card>
              <CardContent className="p-0">
                {loadingUsers ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 rounded bg-muted animate-pulse" />
                    ))}
                  </div>
                ) : filteredUsers.length > 0 ? (
                  <div className="divide-y divide-border">
                    {filteredUsers.map((user, index) => (
                      <div 
                        key={user.id} 
                        className="p-4 animate-fadeIn"
                        style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
                        data-testid={`user-item-${index}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold truncate">{user.name}</p>
                              {getStatusBadge(user.account_status, user.access_expires_at)}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                          <span>
                            Member since: {format(parseISO(user.created_at), "MMM d, yyyy")}
                          </span>
                          <span>
                            Expires: {user.access_expires_at 
                              ? format(parseISO(user.access_expires_at), "MMM d, yyyy")
                              : "Not set"
                            }
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2">
                          {user.account_status === "active" ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSuspendUserDirect(user.id)}
                              className="h-8 text-xs rounded-full"
                              data-testid={`user-suspend-${index}`}
                            >
                              <Pause size={12} className="mr-1" />
                              Suspend
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReactivateUser(user.id)}
                              className="h-8 text-xs rounded-full"
                              data-testid={`user-reactivate-${index}`}
                            >
                              <Play size={12} className="mr-1" />
                              Reactivate
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditExpiry(user)}
                            className="h-8 text-xs rounded-full"
                            data-testid={`user-edit-expiry-${index}`}
                          >
                            <Calendar size={12} className="mr-1" />
                            Edit Expiry
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExtendAccess(user.id)}
                            className="h-8 text-xs rounded-full"
                            data-testid={`user-extend-${index}`}
                          >
                            <CalendarPlus size={12} className="mr-1" />
                            +1 Year
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenResetPassword(user)}
                            className="h-8 text-xs rounded-full"
                            data-testid={`user-reset-pwd-${index}`}
                          >
                            <KeyRound size={12} className="mr-1" />
                            Reset Pwd
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setUserToSuspend(user);
                              setSuspendDialogOpen(true);
                            }}
                            className="h-8 text-xs rounded-full text-orange-600 hover:text-orange-700 border-orange-300 hover:border-orange-400"
                            data-testid={`user-suspend-${index}`}
                          >
                            <Pause size={12} className="mr-1" />
                            Suspend
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 text-xs rounded-full text-destructive hover:text-destructive border-destructive/50 hover:border-destructive"
                            data-testid={`user-delete-${index}`}
                          >
                            <Trash2 size={12} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground" strokeWidth={1} />
                    <p className="text-muted-foreground">
                      {userSearch ? "No members found" : "No members yet"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Authorized Emails Tab */}
          <TabsContent value="emails" className="space-y-4 mt-4">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold font-heading">{emails.length}</div>
                  <div className="text-sm text-muted-foreground">Total Authorized</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold font-heading">
                    {emails.filter(e => e.used).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Registered</div>
                </CardContent>
              </Card>
            </div>

            {/* Add Email Button */}
            <Drawer open={emailDrawerOpen} onOpenChange={(open) => {
              setEmailDrawerOpen(open);
              if (!open) resetEmailForm();
            }}>
              <DrawerTrigger asChild>
                <Button className="w-full h-12 rounded-full" data-testid="add-email-btn">
                  <Plus size={18} className="mr-2" />
                  Authorize New Email
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle className="font-heading">Authorize New Email</DrawerTitle>
                </DrawerHeader>
                
                <div className="px-4 pb-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={emailForm.email}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, email: e.target.value }))}
                      className="h-12"
                      data-testid="admin-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="e.g., Purchased on Jan 1, 2024"
                      value={emailForm.notes}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="min-h-[80px]"
                      data-testid="admin-notes-input"
                    />
                  </div>
                </div>

                <DrawerFooter className="pt-2">
                  <Button 
                    onClick={handleSubmitEmail} 
                    disabled={submittingEmail}
                    className="h-12 rounded-full"
                    data-testid="admin-save-btn"
                  >
                    {submittingEmail ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Authorizing...
                      </>
                    ) : (
                      "Authorize Email"
                    )}
                  </Button>
                  <DrawerClose asChild>
                    <Button variant="outline" className="h-12 rounded-full">Cancel</Button>
                  </DrawerClose>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

            {/* Email List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail size={18} />
                  Authorized Emails
                </CardTitle>
                <CardDescription>
                  Only these emails can register for the app
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loadingEmails ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 rounded bg-muted animate-pulse" />
                    ))}
                  </div>
                ) : emails.length > 0 ? (
                  <div className="divide-y divide-border">
                    {emails.map((email, index) => (
                      <div 
                        key={email.id} 
                        className="admin-email-item animate-fadeIn"
                        style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
                        data-testid={`email-item-${index}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{email.email}</p>
                            <span className={`admin-email-status ${email.used ? "used" : "pending"}`}>
                              {email.used ? (
                                <>
                                  <CheckCircle2 size={10} className="mr-1" />
                                  Registered
                                </>
                              ) : (
                                <>
                                  <Clock size={10} className="mr-1" />
                                  Pending
                                </>
                              )}
                            </span>
                          </div>
                          {email.notes && (
                            <p className="text-sm text-muted-foreground truncate mt-0.5">
                              {email.notes}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Added {format(parseISO(email.created_at), "MMM d, yyyy")}
                          </p>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEmail(email.id)}
                          className="h-9 w-9 rounded-full text-destructive hover:text-destructive flex-shrink-0"
                          data-testid={`email-delete-${index}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Mail className="h-12 w-12 mx-auto mb-3 text-muted-foreground" strokeWidth={1} />
                    <p className="text-muted-foreground">No authorized emails yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

 
      {/* Suspend User Confirmation Dialog */}
      <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Pause className="h-5 w-5 text-orange-500" />
              Suspend User Account?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <span className="block">This will suspend <strong>{userToSuspend?.email}</strong>'s account.</span>
                <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                  <li>User will not be able to log in</li>
                  <li>All their data (protocols, logs, vials) will be preserved</li>
                  <li>You can reactivate them later</li>
                </ul>
                <span className="block mt-2 text-muted-foreground text-sm">This is a soft suspension - data is preserved.</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={suspending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSuspendUser}
              disabled={suspending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {suspending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suspending...
                </>
              ) : (
                "Suspend Account"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Permanently Delete User?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <span className="block">This will permanently delete <strong>{userToDelete?.email}</strong>'s account.</span>
                <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                  <li>All user data (protocols, logs, vials) will be permanently removed</li>
                  <li>User will be removed from authorized emails list</li>
                  <li>This action cannot be undone</li>
                </ul>
                <span className="block mt-2 text-destructive text-sm font-medium">⚠️ This is permanent deletion - consider suspending instead.</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Permanently Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Expiration Drawer */}
      <Drawer open={editExpiryDrawerOpen} onOpenChange={(open) => {
        setEditExpiryDrawerOpen(open);
        if (!open) {
          setUserToEdit(null);
          setNewExpiryDate("");
        }
      }}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Edit Access Expiration</DrawerTitle>
          </DrawerHeader>
          
          <div className="px-4 pb-4 space-y-4">
            {userToEdit && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold">{userToEdit.name}</p>
                <p className="text-sm text-muted-foreground">{userToEdit.email}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>New Expiration Date</Label>
              <DatePicker
                value={newExpiryDate}
                onChange={setNewExpiryDate}
                data-testid="edit-expiry-date-input"
              />
              <p className="text-xs text-muted-foreground">
                Set any date for monthly, trial, or extended memberships
              </p>
            </div>
          </div>

          <DrawerFooter className="pt-2">
            <Button 
              onClick={handleSaveExpiry} 
              disabled={updatingExpiry || !newExpiryDate}
              className="h-12 rounded-full"
              data-testid="save-expiry-btn"
            >
              {updatingExpiry ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Expiration Date"
              )}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="h-12 rounded-full">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Reset Password Drawer */}
      <Drawer open={resetPasswordDrawerOpen} onOpenChange={(open) => {
        setResetPasswordDrawerOpen(open);
        if (!open) {
          setUserToReset(null);
          setTempPassword("");
        }
      }}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Reset User Password</DrawerTitle>
          </DrawerHeader>
          
          <div className="px-4 pb-4 space-y-4">
            {userToReset && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold">{userToReset.name}</p>
                <p className="text-sm text-muted-foreground">{userToReset.email}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <Input
                type="text"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Enter temporary password (min 6 chars)"
                className="h-12"
                data-testid="temp-password-input"
              />
              <p className="text-xs text-muted-foreground">
                The user will be required to change this password on their next login.
              </p>
            </div>
          </div>

          <DrawerFooter className="pt-2">
            <Button 
              onClick={handleResetPassword} 
              disabled={resettingPassword || !tempPassword || tempPassword.length < 6}
              className="h-12 rounded-full"
              data-testid="reset-pwd-submit-btn"
            >
              {resettingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="h-12 rounded-full">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
